import React, { useEffect, useState } from "react";
import fetch from "./shared/Fetch";
import AppHeading from "./sharedComponents/AppHeading";
import Spinner from "./sharedComponents/Spinner";
import styled, { createGlobalStyle } from "styled-components";
import { ReactComponent as VariantLogo } from "./svgs/VariantLogo.svg";

const PrintStyles = createGlobalStyle`
  @media print {
    body * { visibility: hidden; }
    #billing-print-area,
    #billing-print-area * { visibility: visible; }
    #billing-print-area {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
    }
    .no-print { display: none !important; }
  }
`;

const Container = styled.div`
  padding: 2rem;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const QuarterSelect = styled.select`
  font-size: 1rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-lightergrey);
  border-radius: 0.2rem;
`;

const PrintButton = styled.button`
  display: inline-block;
  padding: 0.4rem 1rem;
  background: var(--button-background-color);
  color: var(--button-primary-color);
  border: 1px solid var(--button-primary-color);
  border-radius: 0.2rem;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover {
    background: var(--button-hover-background-color);
    color: var(--button-hover-color);
    border-color: var(--button-hover-color);
  }
`;

/* ── Print document ── */

const Doc = styled.div`
  font-family: sans-serif;
  padding: 3rem;
  max-width: 960px;
  @media screen {
    border: 1px solid var(--color-lightergrey);
    margin-top: 1.5rem;
  }
`;

const DocTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.5rem;
`;

const DocTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin: 0;
  letter-spacing: -0.02em;
`;

const StyledLogo = styled(VariantLogo)`
  height: 2rem;
  width: auto;
`;

const Meta = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.1rem 1rem;
  font-size: 0.95rem;
  margin-bottom: 2.5rem;
`;

const MetaLabel = styled.span`
  color: #666;
`;

const MetaValue = styled.span`
  font-weight: 700;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 0.88rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.4rem 0.75rem;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  font-weight: 600;
  white-space: nowrap;
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  padding: 0.4rem 0.75rem;
  border: 1px solid #e0e0e0;
`;

const TdRight = styled(Td)`
  text-align: right;
`;

const TotalAmountRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 1.5rem;
  margin-top: 2rem;
  font-size: 1rem;
`;

const TotalLabel = styled.span`
  color: #666;
`;

const TotalValue = styled.span`
  font-size: 1.4rem;
  font-weight: 900;
  letter-spacing: -0.01em;
`;

const DocFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e0e0e0;
  font-size: 0.8rem;
  color: #888;
`;

const FooterLogo = styled(VariantLogo)`
  height: 1rem;
  width: auto;
`;

const SUPERADMIN_EMAIL = "oms@variant.no";

function formatNOK(amount) {
  return amount.toLocaleString("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0
  });
}

function currentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `${now.getFullYear()}-Q${q}`;
}

function quarterMonths(quarterStr) {
  const [year, q] = quarterStr.split("-Q");
  const y = Number(year);
  const startMonth = (Number(q) - 1) * 3 + 1;
  return [0, 1, 2].map(i => {
    const m = startMonth + i;
    return `${y}-${String(m).padStart(2, "0")}`;
  });
}

function monthLabel(yearMonth) {
  const [year, month] = yearMonth.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleString("nb-NO", {
    month: "long"
  });
}

function isActiveInMonth(consultant, yearMonth) {
  const [year, month] = yearMonth.split("-").map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const start = consultant.StartDate ? new Date(consultant.StartDate) : null;
  const end = consultant.EndDate ? new Date(consultant.EndDate) : null;
  if (start && start > lastDay) return false;
  if (end && end < lastDay) return false;
  return true;
}

function buildQuarterOptions() {
  const options = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
    for (let q = 4; q >= 1; q--) {
      if (y === now.getFullYear() && q > Math.floor(now.getMonth() / 3) + 1)
        continue;
      options.push(`${y}-Q${q}`);
    }
  }
  return options;
}

function BillingReport({ match }) {
  const {
    departments,
    groups,
    seniorities,
    serviceareas,
    weeks,
    offset,
    availableOnly
  } = match.params;

  const [orgName, setOrgName] = useState("");
  const [pricePerUser, setPricePerUser] = useState(0);
  const [depList, setDepList] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [waitingForData, setWaitingForData] = useState(true);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    (async () => {
      setWaitingForData(true);
      try {
        const user = await fetch("/user/current");
        if (!user || user.email !== SUPERADMIN_EMAIL) return;
        setIsAllowed(true);
        const billingData = await fetch("/billing/organizations");
        if (
          billingData &&
          Array.isArray(billingData.organizations) &&
          billingData.organizations[0]
        ) {
          setOrgName(billingData.organizations[0].OrganizationName);
          setPricePerUser(billingData.organizations[0].PricePerUser || 0);
        }
        if (billingData && Array.isArray(billingData.consultants)) {
          setConsultants(billingData.consultants);
        }
        if (billingData && Array.isArray(billingData.departments)) {
          setDepList(billingData.departments);
        }
      } catch (e) {
        console.error("Failed to fetch billing data", e);
      } finally {
        setWaitingForData(false);
      }
    })();
  }, []);

  const months = quarterMonths(selectedQuarter);
  const quarterOptions = buildQuarterOptions();
  const quarterLabel = selectedQuarter.replace("-Q", " Q");
  const today = new Date().toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  const rows = depList
    .map(dep => {
      const depConsultants = consultants.filter(c => c.DepartmentId === dep.id);
      const monthlyCounts = months.map(
        m => depConsultants.filter(c => isActiveInMonth(c, m)).length
      );
      const quarterTotal = monthlyCounts.reduce((s, n) => s + n, 0);
      const quarterCost = quarterTotal * pricePerUser;
      return { name: dep.Name, monthlyCounts, quarterTotal, quarterCost };
    })
    .filter(r => r.quarterTotal > 0);

  const totalCounts = months.map((_, i) =>
    rows.reduce((s, r) => s + r.monthlyCounts[i], 0)
  );
  const grandTotal = rows.reduce((s, r) => s + r.quarterTotal, 0);
  const grandCost = rows.reduce((s, r) => s + r.quarterCost, 0);

  return (
    <div className="module">
      <PrintStyles />
      <AppHeading
        departments={match.params.departments}
        offset={offset}
        weeks={weeks}
        groups={groups}
        availableOnly={availableOnly}
        seniorities={seniorities}
        serviceareas={serviceareas}
        active="billing"
      />
      <Container>
        <div className="no-print">
          <h2 style={{ marginBottom: "1rem" }}>Fakturaunderlag</h2>
          <Controls>
            <label htmlFor="billing-quarter">Kvartal:</label>
            <QuarterSelect
              id="billing-quarter"
              value={selectedQuarter}
              onChange={e => setSelectedQuarter(e.target.value)}
            >
              {quarterOptions.map(q => (
                <option key={q} value={q}>
                  {q.replace("-Q", " Q")}
                </option>
              ))}
            </QuarterSelect>
            {!waitingForData && isAllowed && (
              <PrintButton onClick={() => window.print()}>
                Last ned PDF — {quarterLabel}
              </PrintButton>
            )}
          </Controls>
        </div>
        <Spinner show={waitingForData} />
        {!waitingForData && !isAllowed && (
          <p>Du har ikke tilgang til denne siden.</p>
        )}
        {!waitingForData && isAllowed && (
          <div id="billing-print-area">
            <Doc>
              <DocTop>
                <DocTitle>Fakturaunderlag</DocTitle>
                <StyledLogo />
              </DocTop>
              <Meta>
                <MetaLabel>Kunde</MetaLabel>
                <MetaValue>{orgName}</MetaValue>
                <MetaLabel>Periode</MetaLabel>
                <MetaValue>{quarterLabel}</MetaValue>
                <MetaLabel>Generert</MetaLabel>
                <MetaValue>{today}</MetaValue>
              </Meta>
              <Table>
                <thead>
                  <tr>
                    <Th>Avdeling</Th>
                    {months.map(m => (
                      <ThRight key={m}>{monthLabel(m)}</ThRight>
                    ))}
                    <ThRight>Totalt</ThRight>
                    <ThRight>Pris/bruker</ThRight>
                    <ThRight>Sum kvartal</ThRight>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(
                    ({ name, monthlyCounts, quarterTotal, quarterCost }) => (
                      <tr key={name}>
                        <Td>{name}</Td>
                        {monthlyCounts.map((count, i) => (
                          <TdRight key={i}>{count}</TdRight>
                        ))}
                        <TdRight>{quarterTotal}</TdRight>
                        <TdRight>
                          {pricePerUser ? formatNOK(pricePerUser) : "—"}
                        </TdRight>
                        <TdRight>
                          {pricePerUser ? formatNOK(quarterCost) : "—"}
                        </TdRight>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
              <TotalAmountRow>
                <TotalLabel>Total amount</TotalLabel>
                <TotalValue>{formatNOK(grandCost)}</TotalValue>
              </TotalAmountRow>
              <DocFooter>
                <span>Fakturaunderlag · {quarterLabel}</span>
                <FooterLogo />
              </DocFooter>
            </Doc>
          </div>
        )}
      </Container>
    </div>
  );
}

export default BillingReport;
